import React from 'react';

import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import uniqueId from 'lodash/uniqueId';
import partition from 'lodash/partition';

import { escapeRegexString } from '@xmatters/common/utils/regexUtil';
import { RecipientType, SearchType } from 'xm-constants/recipientSearch';
import { ScriptStepSubjectDef } from 'xm-constants/scriptStepPermissionDef';
import { DELETED_USER_TYPES } from 'xm-constants/incidentManagement';
import { LicenseTypes } from 'xm-constants/recipients';

/**
 * Given a list, return a regex string that matches every element in the list.
 * Ideally the list should be only strings and numbers, but if other elements are in,
 * they'll be stripped out and a console warning will be shown.
 * @param {any[]} values
 * @returns {string}
 */
export const generateRegexString = (values = []) => {
  const [acceptedValues, notAcceptedValues] = partition(
    values,
    v =>
      (typeof v === 'string' && v !== '') ||
      (typeof v === 'number' && !Number.isNaN(v)),
  );

  if (notAcceptedValues.length) {
    // eslint-disable-next-line no-console
    console.error(
      'Error: skipping these not accepted values:',
      notAcceptedValues,
    );
  }

  return acceptedValues
    .map(str => escapeRegexString(typeof str === 'string' ? str : `${str}`))
    .join('|');
};

const getSearchTermRegex = (
  searchTerms = [],
  searchType = SearchType.CONTAINS.value,
) => {
  let regexString;

  switch (searchType) {
    case SearchType.STARTS_WITH.value:
      regexString = `^(${generateRegexString(searchTerms)})`;
      break;
    case SearchType.ENDS_WITH.value:
      regexString = `(${generateRegexString(searchTerms)})$`;
      break;
    case SearchType.EQUALS.value:
      regexString = `^(${generateRegexString(searchTerms)})$`;
      break;
    default:
      regexString = `(${generateRegexString(searchTerms)})`;
      break;
  }

  return new RegExp(regexString, 'gi');
};

export const getDisplayNameComponentRegex = ({
  description,
  firstName,
  lastName,
  name,
  owner,
  recipientType,
  targetName,
}) => {
  let regexString;
  let stringComponents;

  switch (recipientType) {
    case 'PERSON':
    case 'WEB_SERVICE_USER':
      stringComponents = [firstName, lastName, targetName];
      regexString = `(${generateRegexString(stringComponents)})`;
      break;
    case 'DEVICE':
      stringComponents = owner
        ? [owner.firstName, owner.lastName, owner.targetName, name]
        : [targetName];
      regexString = `(${generateRegexString(stringComponents)})`;
      break;
    case 'GROUP':
    case 'TEAM':
    case 'DYNAMIC_TEAM':
      stringComponents = [targetName];

      if (description) {
        stringComponents.push(description);
      }

      regexString = `(${generateRegexString(stringComponents)})`;
      break;
    default:
      regexString = `(${escapeRegexString(targetName)})`;
      break;
  }

  return new RegExp(regexString, 'gi');
};

export function longestStringFirst(firstString, secondString) {
  return secondString.length - firstString.length;
}

export const getHighlightedSearch = ({
  displayName = '',
  item,
  search = '',
  highlightClass = '',
  searchType = SearchType.CONTAINS.value,
}) => {
  if (!item) {
    return '';
  }

  // for other search types, the search terms are space delimited
  // sort the searchTerms from longest to shortest so it matches the longest first
  const searchTerms = filter(search.split(' '), t => t.trim().length > 0).sort(
    longestStringFirst,
  );
  if (!searchTerms.length) {
    return displayName;
  }

  const searchTermRegex = getSearchTermRegex(searchTerms, searchType);
  const displayNameComponentRegex = getDisplayNameComponentRegex(item);

  const displayNameComponents = displayName.split(displayNameComponentRegex);

  return displayNameComponents.map(str =>
    str.split(searchTermRegex).map(searchToken => {
      const key = uniqueId('display_token');
      if (searchTermRegex.test(searchToken)) {
        return (
          <em key={key} className={highlightClass}>
            {searchToken}
          </em>
        );
      }
      return searchToken ? <span key={key}>{searchToken}</span> : '';
    }),
  );
};

export const buildIconClass = (recipientType, deviceType) => {
  switch (recipientType) {
    case 'DEVICE':
      switch (deviceType) {
        case 'ANDROID_PUSH':
          return 'icon-android';
        case 'APPLE_PUSH':
          return 'icon-apple';
        case 'EMAIL':
          return 'icon-email';
        case 'VOICE':
          return 'icon-phone';
        case 'VOICE_IVR':
          return 'icon-bullhorn';
        case 'TEXT_PAGER':
        case 'NUMERIC_PAGER':
          return 'icon-pager';
        case 'IM':
          return 'icon-comments';
        case 'TEXT_PHONE':
          return 'icon-comment';
        case 'BROWSER':
          return 'icon-desktop';
        case 'GENERIC':
          return 'icon-generic-device';
        case 'VIRTUAL':
          return 'icon-terminal';
        case 'FAX':
          return 'icon-fax';
        case 'BES':
        case 'BLACKBERRY_PUSH':
          return 'icon-black-berry';
        default:
          return 'icon-mobile';
      }
    case 'ROLE':
      return 'icon-role';
    case 'GROUP':
    case 'TEAM':
      return 'icon-group';
    case 'DYNAMIC_TEAM':
      return 'icon-dynamic-team';
    case 'PERSON':
    case 'WEB_SERVICE_USER':
      return 'icon-user';
    case 'ALL_USERS':
      return 'icon-all-users';
    case 'SERVICE':
      return 'icon-settings';
    case 'SITE':
      return 'icon-globe';
    default:
      return '';
  }
};

export function buildSelectedIconClass(item, selectedRecipients) {
  const { recipientType, deviceType, targetName, id } = item;

  if (item && selectedRecipients && selectedRecipients.length) {
    const found = find(
      selectedRecipients,
      recipient =>
        recipient.id === id ||
        // needed to compare recipients on GWT pages
        (recipient.targetName === targetName &&
          recipient.recipientType === recipientType &&
          (recipient.deviceType ? recipient.deviceType === deviceType : true)),
    );

    if (found) {
      return 'icon-checkmark higlighted';
    }
  }

  return buildIconClass(recipientType, deviceType);
}

export function isSelected(item, selectedRecipients) {
  if (!item) return false;
  const { recipientType, deviceType, targetName, id } = item;
  return (
    item &&
    selectedRecipients &&
    selectedRecipients.length &&
    find(
      selectedRecipients,
      recipient =>
        recipient.id === id ||
        // needed to compare recipients on GWT pages
        (recipient.targetName === targetName &&
          recipient.recipientType === recipientType &&
          (recipient.deviceType ? recipient.deviceType === deviceType : true)),
    )
  );
}

export const formatDisplayNameObject = recipientObj => {
  if (!recipientObj) {
    return {};
  }

  const {
    description = '',
    firstName = '',
    lastName = '',
    name = '',
    owner = {},
    recipientType = '',
    type = '',
    targetName = '',
  } = recipientObj;

  switch (recipientType || type) {
    case 'PERSON':
    case 'WEB_SERVICE_USER':
      return firstName && lastName
        ? {
            name: `${firstName} ${lastName}${
              targetName ? ` (${targetName})` : ''
            }`,
          }
        : { name: '' };
    case 'DEVICE':
      return owner.firstName && owner.lastName && owner.targetName && name
        ? {
            name: `${owner.firstName} ${owner.lastName} (${owner.targetName}) - ${name}`,
          }
        : { name: targetName };
    case 'TEAM':
    case 'DYNAMIC_TEAM':
    case ScriptStepSubjectDef.allUsers:
    case ScriptStepSubjectDef.group:
    case 'SERVICE':
      return { name: targetName, description };
    case 'SITE':
      return { name: targetName };
    case 'DELETED_USER':
      return { name: 'Deleted User' };
    default:
      return undefined;
  }
};

const convertDisplayNameObjectToDisplayName = displayNameObject => {
  if (!displayNameObject || !displayNameObject.name) {
    return '';
  }
  return displayNameObject.description
    ? `${displayNameObject.name} - ${displayNameObject.description}`
    : displayNameObject.name;
};

// Be careful importing this function directly because it doesn't handle deleted users.
// See if formatName function from the same file is better for your use case.
export const formatDisplayName = recipientObj => {
  const nameWithDescription = formatDisplayNameObject(recipientObj);
  return convertDisplayNameObjectToDisplayName(nameWithDescription);
};

/**
 * Check if the recipient is inactive.
 * @param {object} recipient - the recipient object
 * @returns {boolean} true if the recipient is inactive, false if not.
 */
export const isInactive = recipient =>
  recipient?.status === 'INACTIVE' || recipient?.owner?.status === 'INACTIVE';

/**
 * Test if a user is deleted or not
 * @param {object} recipient - the recipient object, which can be null.
 * @param {object} isTimeline - if the name being displayed is in timeline. Remove this after the server implementation for timeline is finished.
 * @returns {boolean} - true if the user is deleted. false if not.
 */
export function isDeleted(recipient, isTimeline = false) {
  return (
    isNil(recipient) || // in incident console
    recipient.isDeleted || // after filled with getFilledRecipient function
    (isTimeline ? isInactive(recipient) : false) // Once timeline server changes are done, remove this line because inactive is not the same as deleted.
  );
}

export function formatName(user, intl) {
  return isDeleted(user) || isInactive(user)
    ? intl.formatMessage({ id: 'INCIDENT_DELETED_USER' })
    : formatDisplayName(user);
}

/**
 * @typedef {object} RecipientConfig
 * @property {object} recipient - the recipient object
 * @property {object} intl- the intl object
 * @property {boolean} isTimeline - whether this is a timeline entry. Can remove this once the server turns null for deleted users
 * @property {string} timelineUserType - the type of user in a timeline. Remove this once the server turns null for deleted users
 *
 * Eventually we'll remove isTimeline & timelineUserType once server implementation is updated.
 */

/**
 * Similar to the formatName function above; use this one if:
 * 1. You want to differentiate inactive users from deleted users.
 * 2. You want to display different "Deleted **" for different type of recipients.
 *
 * You likely want to use this function instead of formatName or formatDisplayName if:
 * 1. You're using this in the incident console page.
 * 2. You don't want to use strikethrough for deleted users.
 *
 * Check with Charlie Guan for more info.
 *
 * @params {RecipientConfig} config
 * @returns {string}
 */
export function formatRecipientName({
  recipient,
  intl = undefined,
  isTimeline = false,
  timelineUserType,
}) {
  if (!isDeleted(recipient, isTimeline)) {
    return formatDisplayName(recipient);
  }

  const { type } = recipient ?? {};

  switch (isTimeline ? timelineUserType : type) {
    case DELETED_USER_TYPES.INCIDENT_COMMANDER:
      // Don't want missing intl to crash the application again. Had this happen way too many times
      return intl
        ? intl.formatMessage({ id: 'INCIDENT_DELETED_USER' })
        : 'Deleted User';
    case DELETED_USER_TYPES.STAKEHOLDER:
      return intl
        ? intl.formatMessage({ id: 'INCIDENT_DELETED_STAKEHOLDER' })
        : 'Deleted Stakeholder';
    case DELETED_USER_TYPES.RESOLVER:
    default:
      return intl
        ? intl.formatMessage({ id: 'INCIDENT_DELETED_RESOLVER' })
        : 'Deleted Resolver';
  }
}

const addEBProperties = recipient => {
  return {
    targetName: recipient?.userName ?? recipient?.ebUserId,
    id: recipient?.xmUserId,
    recipientType: 'PERSON',
  };
};

export const processApiRecipients = ({
  filteredData,
  isEverbridgeFlowDesigner = false,
}) => {
  return filteredData.map(item => {
    const newItem = isEverbridgeFlowDesigner
      ? {
          ...item,
          ...addEBProperties(item),
        }
      : item;
    const displayNameObj = formatDisplayNameObject(newItem);
    const displayName = convertDisplayNameObjectToDisplayName(displayNameObj);
    return {
      ...newItem,
      displayName,
      displayNameObj,
    };
  });
};

export const addRecipientList = (existingList, listToAdd) => {
  const selectedIds = new Set(existingList.map(v => v.id));
  const newData = listToAdd.filter(v => !selectedIds.has(v.id));
  return [...existingList, ...processApiRecipients({ filteredData: newData })];
};

export const BaseRecipientTypes = [
  RecipientType.DYNAMIC_TEAMS.value,
  RecipientType.GROUPS.value,
  RecipientType.USERS.value,
];

const excludedAllTypes = new Set([
  RecipientType.ALL.value,
  // Map "ALL" to these. Does not include DEVICES: https://jira.xmatters.com/browse/COR-19084
  RecipientType.DEVICES.value,
]);

export const getRecipientTypesArr = ({ recipientType, searchOptions }) => {
  if (recipientType === RecipientType.ALL.value) {
    return searchOptions
      .map(option => option.value)
      .filter(type => !excludedAllTypes.has(type));
  }
  return [recipientType];
};

export function getRecipientDisplayName(recipient) {
  return get(recipient, 'displayName', '');
}

export const MAX_MESSAGE_RECIPIENTS = 700;

export const notDevice = recipient =>
  recipient.recipientType !== RecipientType.DEVICES.value;

export const getLicenseType = recipient => {
  if (!recipient) return null;
  return recipient.licenseType
    ? recipient.licenseType
    : recipient.owner?.licenseType;
};

export const showLicenseType = (licenseType, isStakeholderUsersEnabled) => {
  return (
    licenseType === LicenseTypes.STAKEHOLDER_USER && isStakeholderUsersEnabled
  );
};

export const getIsStakeholderUser = recipient =>
  getLicenseType(recipient) === LicenseTypes.STAKEHOLDER_USER;

export const getSelectableRecipients = recipients =>
  recipients.filter(
    recipient => !showLicenseType(getLicenseType(recipient), true),
  );

export const containsStakeholderUsers = recipients => {
  return recipients.some(recipient =>
    showLicenseType(getLicenseType(recipient), true),
  );
};

export const isActive = recipient =>
  ![RecipientType.USERS.value, RecipientType.GROUPS.value].includes(
    recipient?.recipientType,
  ) || recipient?.status === 'ACTIVE';

export const hasMembers = recipient => {
  if (!recipient) return true;
  if (recipient.recipientType === RecipientType.GROUPS.value) {
    return recipient.hasMembers === undefined ? true : recipient.hasMembers;
  }
  if (recipient.recipientType === RecipientType.SERVICES.value) {
    return recipient.ownedBy !== undefined;
  }
  if (recipient.recipientType === RecipientType.USERS.value) {
    return recipient.hasActiveDevices === undefined
      ? true
      : recipient.hasActiveDevices;
  }
  return true;
};

export const hasEmptyMember = recipients => {
  if (!recipients) return false;
  return !recipients.every(recipient => hasMembers(recipient));
};
